import { useState } from "react";
import { Upload, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { dictionary } from "../../../utils/dictionary";

interface ProductManualInputsProps {
	manualData: any;
	handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleRemoveImage: (index: number) => void;
	handleManualDataChange: (field: string, value: string) => void;
	isUploadingImages?: boolean;
}

export function ProductManualInputs({
	manualData,
	handleImageUpload,
	handleRemoveImage,
	handleManualDataChange,
	isUploadingImages = false
}: ProductManualInputsProps) {
	const { language } = useAuth();
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);
	const images: string[] = manualData.images || [];
	const mainImageUrl = images[selectedImageIndex] || images[0];

	return (
		<div className="w-full h-full flex flex-col md:flex-row gap-6 items-stretch">
			{/* Image column */}
			<div className="flex flex-col gap-3 w-full md:w-[260px] md:max-w-[260px] shrink-0">
				{/* Main image / upload area — fixed height */}
				<div className="w-full h-[260px] rounded-lg overflow-hidden relative">
					{mainImageUrl ? (
						<div className="w-full h-full flex items-center justify-center bg-slate-50 border border-slate-200 rounded-lg">
							<img
								src={mainImageUrl}
								alt={manualData.name || 'Product'}
								className="max-w-full max-h-full object-contain"
							/>
						</div>
					) : (
						<label className="w-full h-full flex flex-col items-center justify-center text-center cursor-pointer bg-[#FCFDFD] border-2 border-dashed border-gray-300 hover:border-fluxi rounded-lg transition-colors">
							<input
								type="file"
								accept="image/*"
								multiple
								onChange={handleImageUpload}
								disabled={isUploadingImages}
								className="hidden"
							/>
							{isUploadingImages ? (
								<>
									<div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-4 shadow-[0_0_15px_rgba(0,0,0,0.15)]">
										<div className="w-6 h-6 border-4 border-gray-200 border-t-fluxi rounded-full animate-spin" />
									</div>
									<p className="text-sm font-semibold text-gray-600">
										{dictionary?.["uploading-images"]?.[language] || "Cargando imágenes..."}
									</p>
								</>
							) : (
								<>
									<div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-4 shadow-[0_0_15px_rgba(0,0,0,0.15)]">
										<Upload className="w-6 h-6 text-fluxi" />
									</div>
									<p className="text-sm font-semibold text-gray-600">
										{dictionary["upload-photo"][language]}
									</p>
									<p className="text-xs text-gray-400 mt-1 px-8">
										{dictionary["accepted-formats"][language]}
									</p>
								</>
							)}
						</label>
					)}
				</div>

				{/* Thumbnails — 4-column grid */}
				{images.length > 0 && (
					<div className="grid grid-cols-4 gap-2">
						{images.map((src: string, index: number) => (
							<div key={`${index}-${src?.slice(-20)}`} className="relative group">
								<button
									type="button"
									onClick={() => setSelectedImageIndex(index)}
									className={`aspect-square w-full rounded overflow-hidden cursor-pointer transition-all ${
										index === selectedImageIndex
											? 'shadow-[0_0_0_1.5px_theme(colors.fluxi.DEFAULT)]'
											: 'shadow-[0_0_0_1px_#e2e8f0] hover:shadow-[0_0_0_1px_#cbd5e1]'
									}`}
								>
									<img src={src} alt="" className="w-full h-full object-cover" />
								</button>
								<button
									type="button"
									onClick={() => {
										handleRemoveImage(index);
										if (selectedImageIndex >= index && selectedImageIndex > 0) {
											setSelectedImageIndex(selectedImageIndex - 1);
										}
									}}
									className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-slate-200 hover:bg-red-50 hover:border-red-300 text-slate-400 hover:text-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-sm"
								>
									<Trash2 className="size-2.5" />
								</button>
							</div>
						))}
						{/* Upload as grid cell */}
						<label
							className={`aspect-square w-full rounded border-2 border-dashed flex flex-col items-center justify-center transition-colors ${
								isUploadingImages
									? 'cursor-wait border-slate-200 bg-slate-50 text-slate-400'
									: 'border-slate-300 hover:border-fluxi hover:bg-slate-50 cursor-pointer text-slate-400 hover:text-fluxi'
							}`}
						>
							<input
								type="file"
								accept="image/*"
								multiple
								onChange={handleImageUpload}
								disabled={isUploadingImages}
								className="hidden"
							/>
							{isUploadingImages ? (
								<span className="w-4 h-4 border-2 border-slate-200 border-t-fluxi rounded-full animate-spin block" />
							) : (
								<Plus className="size-5" />
							)}
						</label>
					</div>
				)}
			</div>

			{/* Details column */}
			<div className="flex-1 flex flex-col gap-4 w-full h-full">
				<div className="flex flex-col gap-1.5">
					<label className="text-[12px] font-semibold text-slate-400 ml-1">
						{dictionary['product-name']?.[language] ?? 'Nombre'}
					</label>
					<input
						type="text"
						placeholder={dictionary["product-name-example-placeholder"]?.[language] ?? "E.g.: Vitamin C facial serum..."}
						value={manualData.name}
						onChange={(e) => handleManualDataChange('name', e.target.value)}
						className="w-full font-semibold text-[14px] text-[#0F172A] leading-tight bg-white border border-slate-200 rounded-[12px] px-3 py-2 focus:border-fluxi focus:shadow-[0_0_0_3px_rgba(62,62,239,0.1)] outline-none transition-all placeholder:text-slate-300"
					/>
				</div>
				<div className="flex flex-col gap-1.5 flex-1 min-h-0">
					<label className="text-[12px] font-semibold text-slate-400 ml-1 shrink-0">
						{dictionary['product-description']?.[language] ?? 'Descripción'}
					</label>
					<textarea
						placeholder={dictionary["product-description-example-placeholder"]?.[language] ?? "Features, benefits, who it's for..."}
						value={manualData.description}
						onChange={(e) => handleManualDataChange('description', e.target.value)}
						className="w-full text-[12px] text-slate-600 leading-relaxed bg-white border border-slate-200 rounded-[12px] px-3 py-2 focus:border-fluxi focus:shadow-[0_0_0_3px_rgba(62,62,239,0.1)] outline-none transition-all resize-none flex-1 min-h-[120px] placeholder:text-slate-300"
					/>
				</div>
			</div>
		</div>
	);
}
